import React, { useState, useEffect } from 'react';
import {
  Save,
  CheckCircle2,
  Clock,
  Phone,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import { db } from '../../firebase/config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';
import { auditService } from '../../services/auditService';
import './AdminPages.css';

export const AdminSettings: React.FC = () => {
  const { user } = useAuth();
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    seatHoldMinutes: 10,
    dailyCutoffHour: '22:00',
    maxAdvanceBookingDays: 30,
    supportPhoneNumber: '01207337330',
    supportWhatsapp: '201207337330',
    systemMaintenance: false,
    currentSemester: 'الفصل الدراسي الأول 2026-2027',
    cancellationWindowHours: 24,
    minDepositRate: 100,
    autoConfirmApprovedReceipts: true,
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'general'));
        if (snap.exists()) {
          setSettings((prev) => ({ ...prev, ...snap.data() }));
        }
      } catch (err) {
        console.warn('Could not load settings from Firestore, using defaults:', err);
      }
    };
    loadSettings();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'general'), settings, { merge: true });
      await auditService.logAction(
        user?.uid || 'admin_omarkhaled',
        user?.email || 'omarkhaled20262@gmail.com',
        'TRIP_UPDATE_STATUS',
        'TRIP',
        'settings_general',
        { seatHoldMinutes: settings.seatHoldMinutes, cutoff: settings.dailyCutoffHour }
      );
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error('Error saving settings to Firestore:', err);
      alert('حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h2>الإعدادات العامة للنظام</h2>
          <p className="admin-page-subtitle">
            تكوين معايير الحجز وقفل المقاعد وخدمة العملاء وأوقات إغلاق الرحلات
          </p>
        </div>
        <div className="admin-page-actions">
          <button
            onClick={handleSave}
            className="btn-primary flex items-center gap-2"
            disabled={isSaving}
          >
            <Save size={16} />
            <span>{isSaving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}</span>
          </button>
        </div>
      </div>

      {saveSuccess && (
        <div className="admin-alert-banner success">
          <CheckCircle2 size={18} />
          <span>تم حفظ وتطبيق جميع إعدادات النظام بنجاح!</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Card 1: Booking Policies */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-slate-100 text-slate-800">
            <Clock size={20} className="text-[#E5232A]" />
            <h3 className="font-bold text-lg">سياسات ومحددات الحجز</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                مدة حجز المقعد المؤقت (Hold Duration بالدقائق)
              </label>
              <input
                type="number"
                min="3"
                max="60"
                value={settings.seatHoldMinutes}
                onChange={(e) =>
                  setSettings({ ...settings, seatHoldMinutes: Number(e.target.value) })
                }
                className="form-control"
              />
              <span className="text-xs text-slate-500 mt-1 block">
                المدة التي يتم خلالها تجميد المقعد للطالب أثناء إتمام الدفع قبل إتاحته لغيره.
              </span>
            </div>

            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                موعد إغلاق الحجز اليومي (Daily Cutoff Time)
              </label>
              <input
                type="time"
                value={settings.dailyCutoffHour}
                onChange={(e) => setSettings({ ...settings, dailyCutoffHour: e.target.value })}
                className="form-control"
              />
              <span className="text-xs text-slate-500 mt-1 block">
                توقيت المساء الذي يتوقف عنده قبول حجز رحلات اليوم التالي مباشرة.
              </span>
            </div>

            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                أقصى فترة للحجز المسبق (بالأيام)
              </label>
              <input
                type="number"
                min="7"
                max="90"
                value={settings.maxAdvanceBookingDays}
                onChange={(e) =>
                  setSettings({ ...settings, maxAdvanceBookingDays: Number(e.target.value) })
                }
                className="form-control"
              />
              <span className="text-xs text-slate-500 mt-1 block">
                عدد الأيام المتاحة في التقويم الشهري ليختار الطالب منها.
              </span>
            </div>

            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                المهلة المسموحة لإلغاء الحجز واسترداد الرصيد (بالساعات قبل الرحلة)
              </label>
              <input
                type="number"
                min="0"
                max="72"
                value={settings.cancellationWindowHours}
                onChange={(e) =>
                  setSettings({ ...settings, cancellationWindowHours: Number(e.target.value) })
                }
                className="form-control"
              />
              <span className="text-xs text-slate-500 mt-1 block">
                إذا ألغى الطالب قبل هذه المهلة، يسترد كامل المبلغ إلى محفظته.
              </span>
            </div>
          </div>
        </div>

        {/* Card 2: Contact & Support */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-slate-100 text-slate-800">
            <Phone size={20} className="text-emerald-600" />
            <h3 className="font-bold text-lg">معلومات الدعم الفني والتواصل</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                رقم هاتف الطوارئ وخدمة الطلاب
              </label>
              <input
                type="text"
                value={settings.supportPhoneNumber}
                onChange={(e) => setSettings({ ...settings, supportPhoneNumber: e.target.value })}
                className="form-control"
                dir="ltr"
              />
            </div>

            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                رقم واتساب خدمة العملاء (مع كود الدولة)
              </label>
              <input
                type="text"
                value={settings.supportWhatsapp}
                onChange={(e) => setSettings({ ...settings, supportWhatsapp: e.target.value })}
                className="form-control"
                dir="ltr"
              />
            </div>
          </div>
        </div>

        {/* Card 3: Academic & Operations */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-slate-100 text-slate-800">
            <Calendar size={20} className="text-indigo-600" />
            <h3 className="font-bold text-lg">العام الأكاديمي والتشغيل</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                الفصل الدراسي الجاري
              </label>
              <input
                type="text"
                value={settings.currentSemester}
                onChange={(e) => setSettings({ ...settings, currentSemester: e.target.value })}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label className="font-medium text-slate-700 text-sm mb-1 block">
                الاعتماد التلقائي للإيصالات عند الموافقة
              </label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="checkbox"
                  id="autoConfirm"
                  checked={settings.autoConfirmApprovedReceipts}
                  onChange={(e) =>
                    setSettings({ ...settings, autoConfirmApprovedReceipts: e.target.checked })
                  }
                  className="w-4 h-4 accent-[#E5232A] rounded border-slate-300"
                />
                <label htmlFor="autoConfirm" className="text-sm text-slate-700 select-none">
                  إصدار تذكرة إلكترونية فورية للطالب بمجرد قبول الإيصال
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Emergency / Maintenance */}
        <div className="bg-white rounded-xl border border-amber-200 p-6 shadow-sm bg-amber-50/30">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-amber-200 text-amber-900">
            <AlertTriangle size={20} className="text-amber-600" />
            <h3 className="font-bold text-lg">وضع الصيانة الاستثنائي</h3>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold text-slate-800 text-sm">وضع الصيانة للنظام</div>
              <div className="text-xs text-slate-500 mt-1">
                عند تفعيل هذا الخيار، سيتم تعليق الحجوزات الجديدة وعرض شاشة صيانة للطلاب لحين
                الانتهاء.
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.systemMaintenance}
                onChange={(e) =>
                  setSettings({ ...settings, systemMaintenance: e.target.checked })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <button
            type="submit"
            className="btn-primary flex items-center gap-2 px-8 py-3"
            disabled={isSaving}
          >
            <Save size={18} />
            <span>{isSaving ? 'جارٍ الحفظ...' : 'حفظ الإعدادات'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
